import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { Ban, Loader2, Copy, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function trunc(a, n = 10) {
  if (!a) return "—";
  return `${a.slice(0, n)}…${a.slice(-4)}`;
}
async function copyText(t) {
  try { await navigator.clipboard.writeText(t); toast.success("Copied"); } catch { /* noop */ }
}

/** Global manual wallet block/unblock tool — block deposit and/or withdrawal
 *  for any address, with a note + timestamp. Self-contained (fetches its own
 *  data). Used on both the Flip and User Wallets admin tabs. */
export default function BlocklistManager({ onChange }) {
  const { getAuthHeaders } = useWallet();
  const [blocked, setBlocked] = useState([]);
  const [form, setForm] = useState({ address: "", block_deposit: true, block_withdrawal: true, note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/blocked-wallets`, { headers: getAuthHeaders() });
      if (res.ok) setBlocked((await res.json()).blocked || []);
    } catch { /* noop */ }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/blocked-wallets`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const j = await res.json();
      if (res.ok && j.success) {
        toast.success(`Blocked ${trunc(form.address, 8)}`);
        setForm({ address: "", block_deposit: true, block_withdrawal: true, note: "" });
        load(); onChange && onChange();
      } else toast.error(j.detail || "Failed to block");
    } catch { toast.error("Block error"); }
    finally { setSaving(false); }
  };

  const unblock = async (address) => {
    if (!window.confirm(`Unblock ${trunc(address)}?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/blocked-wallets/${address}`, {
        method: "DELETE", headers: getAuthHeaders(),
      });
      if (res.ok) { toast.success("Unblocked"); load(); onChange && onChange(); }
      else toast.error("Failed to unblock");
    } catch { toast.error("Unblock error"); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="blocklist-manager">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Ban className="w-5 h-5 text-red-500" /> Wallet Blocklist
          <Badge variant="outline" className="ml-2">{blocked.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border border-zinc-800 rounded-lg p-3 mb-4 space-y-2">
          <Input placeholder="Wallet address (SP…)" value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="bg-zinc-800 border-zinc-700 font-mono text-xs"
            data-testid="block-address-input" />
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.block_deposit}
                onChange={(e) => setForm({ ...form, block_deposit: e.target.checked })}
                data-testid="block-deposit-check" /> Block Deposit
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.block_withdrawal}
                onChange={(e) => setForm({ ...form, block_withdrawal: e.target.checked })}
                data-testid="block-withdrawal-check" /> Block Withdrawal
            </label>
          </div>
          <Input placeholder="Note — why is this wallet blocked?" value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="bg-zinc-800 border-zinc-700" data-testid="block-note-input" />
          <Button className="bg-red-600 hover:bg-red-700" disabled={saving || !form.address}
            onClick={submit} data-testid="block-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
            Block Wallet
          </Button>
        </div>

        {blocked.length === 0 ? (
          <p className="text-sm text-zinc-500 py-2" data-testid="blocklist-empty">No wallets currently blocked.</p>
        ) : (
          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            {blocked.map((b) => (
              <div key={b.address} className="flex flex-wrap items-center justify-between gap-2 border border-zinc-800 rounded-lg p-2" data-testid={`blocked-row-${b.address}`}>
                <div className="min-w-0">
                  <button className="font-mono text-xs text-zinc-300 inline-flex items-center gap-1 hover:text-white"
                    onClick={() => copyText(b.address)}>
                    {trunc(b.address)} <Copy className="w-3 h-3" />
                  </button>
                  <div className="flex gap-1 mt-1">
                    {b.block_deposit && <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30">no deposit</Badge>}
                    {b.block_withdrawal && <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30">no withdrawal</Badge>}
                    {b.source === "wallet_hold" && <Badge variant="outline" className="text-[10px] text-blue-300 border-blue-500/30">hold</Badge>}
                  </div>
                  {b.note && <p className="text-xs text-zinc-500 mt-1">{b.note}</p>}
                  <p className="text-[10px] text-zinc-600 mt-0.5">{b.created_at ? new Date(b.created_at).toLocaleString() : ""}</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 border-zinc-700"
                  onClick={() => unblock(b.address)} data-testid={`unblock-${b.address}`}>
                  <Trash2 className="w-3 h-3 mr-1" /> Unblock
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
